import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import {
  NONE_EXPLANATIONS,
  TOTAL_LABEL,
  cellValue,
  displayLabel,
  entryLabel,
  rowHeaderLabels,
} from "./pivotModel";

// Rows that close the table after the species, sites or periods: the
// algorithm's non-species results and the catch-alls.
const TRAILING = new Set([
  "unknown",
  "bycatch",
  "empty",
  "dirty",
  "unclassified",
  "none",
]);
const isTrailing = (entry) => TRAILING.has(entry?.category?.[0]);

const NUMBER = new Intl.NumberFormat();
// a derived value: its own format, or a count; blank when there is none
const derivedText = (d, v) =>
  v == null || v === "" ? (
    <Box component="span" sx={{ color: "text.disabled" }} aria-label="none">
      –
    </Box>
  ) : d.format ? (
    d.format(v)
  ) : (
    NUMBER.format(v)
  );

const Label = ({ text, note }) =>
  NONE_EXPLANATIONS[text] || note ? (
    <Tooltip title={note ?? NONE_EXPLANATIONS[text]} describeChild>
      <Box
        component="span"
        tabIndex={0}
        sx={{ borderBottom: "1px dotted", cursor: "help" }}
      >
        {text}
      </Box>
    </Tooltip>
  ) : (
    text
  );

const cellSx = {
  px: 1.5,
  py: 0.75,
  whiteSpace: "nowrap",
  borderBottom: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
};
const numberSx = {
  ...cellSx,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
};
const headSx = {
  fontWeight: 500,
  color: "text.primary",
  verticalAlign: "bottom",
};

/**
 * A pivot as a table: the row dimensions down the left (pinned while
 * scrolling), then any derived columns, one column per column entry, a Total
 * count column on the right and a Total count row at the bottom (both pinned).
 * Derived rows, a transposed table's derived columns, sit above the counts.
 * With `onCellClick`, a count opens the specimens behind it.
 */
export const PivotTable = ({ model, onCellClick, caption }) => {
  const headerRefs = useRef([]);
  const [lefts, setLefts] = useState([]);
  const leading = rowHeaderLabels(model);
  const rowSpan = Math.max(leading.length, 1);

  // Pin each row-dimension column at the width of those before it.
  useLayoutEffect(() => {
    const widths = headerRefs.current
      .slice(0, rowSpan)
      .map((el) => el?.offsetWidth ?? 0);
    const next = widths.map((_, i) =>
      widths.slice(0, i).reduce((a, b) => a + b, 0),
    );
    setLefts((prev) => (prev.join() === next.join() ? prev : next));
  }, [model, rowSpan]);

  const pinLeft = (i, z = 1) => ({
    position: "sticky",
    left: lefts[i] ?? 0,
    zIndex: z,
  });
  const lastPinned = (i) =>
    i === rowSpan - 1 ? { borderRight: 1, borderRightColor: "divider" } : {};
  const firstTrailing = model.rows.findIndex(isTrailing);

  const count = (value, r, c) => {
    if (!onCellClick || !value) return NUMBER.format(value);
    const what = [entryLabel(model.rows[r]), entryLabel(model.columns[c])]
      .filter(Boolean)
      .join(", ");
    return (
      <Box
        component="button"
        type="button"
        onClick={() =>
          onCellClick({ row: model.rows[r], column: model.columns[c] })
        }
        aria-label={`${what}: ${NUMBER.format(value)}. Show these specimens`}
        sx={{
          font: "inherit",
          color: "primary.main",
          p: 0,
          border: 0,
          bgcolor: "transparent",
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {NUMBER.format(value)}
      </Box>
    );
  };

  // Shadows on the pinned edges while columns are scrolled under them, so a
  // cut-off column reads as "more this way" (scrollbars are often hidden).
  const scroller = useRef(null);
  const [under, setUnder] = useState({ left: false, right: false });
  const measure = () => {
    const el = scroller.current;
    if (!el) return;
    const next = { left: el.scrollLeft > 0, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1 };
    setUnder((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
  };
  useLayoutEffect(() => {
    measure();
    const el = scroller.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [model]);

  return (
    <Box
      ref={scroller}
      onScroll={measure}
      data-under-left={under.left || undefined}
      data-under-right={under.right || undefined}
      sx={{
        "&[data-under-left] .pinned-left": { boxShadow: "8px 0 8px -6px rgba(0, 0, 0, 0.3)" },
        "&[data-under-right] .pinned-right": { boxShadow: "-8px 0 8px -6px rgba(0, 0, 0, 0.3)" },
        // as tall as the table, up to the space there is; then it scrolls
        flex: "0 1 auto",
        minHeight: 0,
        overflow: "auto",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        fontSize: "0.875rem",
      }}
    >
      <Box
        component="table"
        sx={{ borderCollapse: "separate", borderSpacing: 0, minWidth: "100%" }}
      >
        {caption && (
          <Box
            component="caption"
            sx={{
              position: "absolute",
              // strings: MUI reads a bare 1 as 100%
              width: "1px",
              height: "1px",
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
            }}
          >
            {caption}
          </Box>
        )}
        <Box component="thead">
          <Box component="tr">
            {(leading.length ? leading : [""]).map((label, i) => (
              <Box
                component="th"
                scope="col"
                key={`h${i}`}
                ref={(el) => (headerRefs.current[i] = el)}
                className={i === rowSpan - 1 ? "pinned-left" : undefined}
                sx={{
                  ...cellSx,
                  ...headSx,
                  textAlign: "left",
                  position: "sticky",
                  top: 0,
                  ...pinLeft(i, 3),
                  ...lastPinned(i),
                }}
              >
                {label}
              </Box>
            ))}
            {model.leading.map((d, i) => (
              <Box
                component="th"
                scope="col"
                key={`d${i}`}
                sx={{
                  ...numberSx,
                  ...headSx,
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <Label text={d.label} note={d.note} />
              </Box>
            ))}
            {model.columns.map((col, c) => (
              <Box
                component="th"
                scope="col"
                key={`c${c}`}
                sx={{
                  ...numberSx,
                  ...headSx,
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  color: isTrailing(col) ? "text.secondary" : "text.primary",
                }}
              >
                {col.display?.lines ? (
                  col.display.lines.map((line, i) => (
                    <Box
                      component="span"
                      key={i}
                      sx={{ display: "block", ...(i > 0 && { fontWeight: 400, color: "text.secondary", fontSize: "0.75rem" }) }}
                    >
                      {line}
                    </Box>
                  ))
                ) : (
                  <Label text={entryLabel(col)} />
                )}
              </Box>
            ))}
            {model.totalColumn && (
              <Box
                component="th"
                scope="col"
                className="pinned-right"
                sx={{
                  ...numberSx,
                  ...headSx,
                  position: "sticky",
                  top: 0,
                  right: 0,
                  zIndex: 3,
                  borderLeft: 1,
                  borderLeftColor: "divider",
                }}
              >
                {TOTAL_LABEL}
              </Box>
            )}
          </Box>
        </Box>
        <Box component="tbody">
          {model.leadingRows.map((d, i) => (
            <Box component="tr" key={`dr${i}`}>
              <Box
                component="th"
                scope="row"
                colSpan={rowSpan}
                className="pinned-left"
                sx={{
                  ...cellSx,
                  ...headSx,
                  textAlign: "left",
                  ...pinLeft(0, 1),
                  borderRight: 1,
                  borderRightColor: "divider",
                  ...(i === model.leadingRows.length - 1
                    ? { borderBottomColor: "text.disabled" }
                    : {}),
                }}
              >
                <Label text={d.label} note={d.note} />
              </Box>
              {d.values.map((v, c) => (
                <Box
                  component="td"
                  key={`dv${c}`}
                  sx={{
                    ...numberSx,
                    fontWeight: 500,
                    ...(i === model.leadingRows.length - 1
                      ? { borderBottomColor: "text.disabled" }
                      : {}),
                  }}
                >
                  {derivedText(d, v)}
                </Box>
              ))}
              {model.totalColumn && (
                <Box
                  component="td"
                  className="pinned-right"
                  sx={{
                    ...numberSx,
                    fontWeight: 500,
                    position: "sticky",
                    right: 0,
                    zIndex: 1,
                    borderLeft: 1,
                    borderLeftColor: "divider",
                    ...(i === model.leadingRows.length - 1
                      ? { borderBottomColor: "text.disabled" }
                      : {}),
                  }}
                >
                  {derivedText(d, d.total)}
                </Box>
              )}
            </Box>
          ))}
          {model.rows.map((row, r) => {
            const separate =
              r === firstTrailing && r > 0
                ? { borderTop: 1, borderTopColor: "text.disabled" }
                : {};
            return (
              <Box
                component="tr"
                key={`r${r}`}
                sx={{ "&:hover > *": { bgcolor: "action.hover" } }}
              >
                {(leading.length ? row.label : [""]).map((label, i) => (
                  <Box
                    component="th"
                    scope="row"
                    key={`l${i}`}
                    className={i === rowSpan - 1 ? "pinned-left" : undefined}
                    sx={{
                      ...cellSx,
                      fontWeight: 400,
                      textAlign: "left",
                      color: isTrailing(row)
                        ? "text.secondary"
                        : "text.primary",
                      ...pinLeft(i, 1),
                      ...lastPinned(i),
                      ...separate,
                    }}
                  >
                    {row.display?.lines ? (
                      row.display.lines.map((line, j) => (
                        <Box
                          component="span"
                          key={j}
                          sx={{ ...(j > 0 && { ml: 1, color: "text.secondary", fontSize: "0.75rem" }) }}
                        >
                          {line}
                        </Box>
                      ))
                    ) : (
                      <Label text={displayLabel(label)} />
                    )}
                  </Box>
                ))}
                {model.leading.map((d, i) => (
                  <Box
                    component="td"
                    key={`d${i}`}
                    sx={{ ...numberSx, fontWeight: 500, ...separate }}
                  >
                    {derivedText(d, d.values[r])}
                  </Box>
                ))}
                {model.columns.map((_, c) => {
                  const value = cellValue(model, r, c);
                  return (
                    <Box
                      component="td"
                      key={`v${c}`}
                      sx={{
                        ...numberSx,
                        color: value ? "text.primary" : "text.disabled",
                        ...separate,
                      }}
                    >
                      {count(value, r, c)}
                    </Box>
                  );
                })}
                {model.totalColumn && (
                  <Box
                    component="td"
                    className="pinned-right"
                    sx={{
                      ...numberSx,
                      fontWeight: 500,
                      position: "sticky",
                      right: 0,
                      zIndex: 1,
                      borderLeft: 1,
                      borderLeftColor: "divider",
                      ...separate,
                    }}
                  >
                    {NUMBER.format(model.rowTotals[r] ?? 0)}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        {model.totalRow && (
          <Box component="tfoot">
            <Box component="tr">
              <Box
                component="th"
                scope="row"
                colSpan={rowSpan}
                className="pinned-left"
                sx={{
                  ...cellSx,
                  ...headSx,
                  textAlign: "left",
                  position: "sticky",
                  bottom: 0,
                  left: 0,
                  zIndex: 3,
                  borderTop: 1,
                  borderTopColor: "divider",
                  borderRight: 1,
                  borderRightColor: "divider",
                }}
              >
                {TOTAL_LABEL}
              </Box>
              {model.leading.map((d, i) => (
                <Box
                  component="td"
                  key={`dt${i}`}
                  sx={{
                    ...numberSx,
                    fontWeight: 500,
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2,
                    borderTop: 1,
                    borderTopColor: "divider",
                  }}
                >
                  {derivedText(d, d.total)}
                </Box>
              ))}
              {model.columnTotals.map((total, c) => (
                <Box
                  component="td"
                  key={`t${c}`}
                  sx={{
                    ...numberSx,
                    fontWeight: 500,
                    position: "sticky",
                    bottom: 0,
                    zIndex: 2,
                    borderTop: 1,
                    borderTopColor: "divider",
                  }}
                >
                  {NUMBER.format(total)}
                </Box>
              ))}
              {model.totalColumn && (
                <Box
                  component="td"
                  className="pinned-right"
                  sx={{
                    ...numberSx,
                    fontWeight: 600,
                    position: "sticky",
                    bottom: 0,
                    right: 0,
                    zIndex: 3,
                    borderTop: 1,
                    borderTopColor: "divider",
                    borderLeft: 1,
                    borderLeftColor: "divider",
                  }}
                >
                  {NUMBER.format(model.grandTotal)}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
